import { useState } from 'react'

import { useAppSelector } from 'app-redux/hooks'

import ManaCurveV2 from 'components/deckPage/charts/ManaCurveV2'
import PhatDropdown from 'components/formElements/PhatDropdown'
import PhatButton from 'components/formElements/PhatButton'
import { SliceType, selectCardsByChartType } from 'components/deckPage/charts/selectCardsByChartType'

import styles from './manaCurves.module.scss'

type Props = {
  neverHideCharts?: boolean
  tallerPrimaryChart?: boolean
}

const ManaCurves = ({ neverHideCharts, tallerPrimaryChart }: Props) => {
  const [sliceType, setSliceType] = useState<SliceType>('color')
  const [shownChart, setShownChart] = useState<string | null>(null)

  const cardMap = useAppSelector(state => state.deck.cardMap)
  const categories = useAppSelector(state => state.deck.categories)

  const deck = Object.values(cardMap).filter(card => {
    const primaryCategoryName = card.categories[0]
    const category = categories[primaryCategoryName]

    if (primaryCategoryName === 'Sideboard' || !category?.includedInDeck) return false

    return true
  })

  const { groupedData: groupedData, relativeMax } = selectCardsByChartType(deck, sliceType)

  const handleSetSlice = (slice: SliceType) => {
    const { groupedData: updatedGroups } = selectCardsByChartType(deck, slice)

    setSliceType(slice)

    if (slice === 'color' || neverHideCharts) setShownChart(null)
    else setShownChart(updatedGroups[0]?.key)
  }

  return (
    <div className={styles.container}>
      <ManaCurveV2 cards={deck} showAverageManaValue showTotalManaValue />

      <PhatDropdown
        options={[
          { label: 'Color', id: 'color', onClick: () => handleSetSlice('color') },
          {
            label: 'Permanent / non-permanent',
            id: 'permanentVSpell',
            onClick: () => handleSetSlice('permanentVSpell'),
          },
          { label: 'Categories', id: 'categories', onClick: () => handleSetSlice('categories') },
          { label: 'Types', id: 'types', onClick: () => handleSetSlice('types') },
          { label: 'Subtypes', id: 'subtypes', onClick: () => handleSetSlice('subtypes') },
          { label: 'Rarity', id: 'rarity', onClick: () => handleSetSlice('rarity') },
          { label: 'Power', id: 'power', onClick: () => handleSetSlice('power') },
          { label: 'Toughness', id: 'toughness', onClick: () => handleSetSlice('toughness') },
          { label: 'Loyalty', id: 'loyalty', onClick: () => handleSetSlice('loyalty') },
        ]}
        inlineHeader
        header="Mana curve by"
        value={sliceType}
      />

      {shownChart !== null && (
        <PhatDropdown
          options={groupedData.map(group => ({
            label: group.label,
            id: group.key,
            onClick: () => setShownChart(group.key),
          }))}
          inlineHeader
          header="Current chart"
          value={shownChart}
        />
      )}

      <div className={styles.specificColors}>
        {groupedData.map((group, index) => {
          if (shownChart !== null && shownChart !== group.key) return null

          return (
            <ManaCurveV2
              key={index}
              cards={group.cards}
              customLabel={group.label}
              className={styles.secondaryCharts}
              barChartClassName={group.className}
              relativeMaximum={relativeMax}
              hideEmptyCharts={sliceType !== 'color'}
              dimEmptyCharts={sliceType === 'color'}
            />
          )
        })}
      </div>

      {shownChart !== null && !neverHideCharts && (
        <PhatButton className={styles.showMoreButton} onClick={() => setShownChart(null)}>
          Show all charts
        </PhatButton>
      )}
      {shownChart === null && !neverHideCharts && (
        <PhatButton className={styles.showMoreButton} onClick={() => setShownChart(groupedData[0]?.key)}>
          Show only one chart
        </PhatButton>
      )}
    </div>
  )
}

export default ManaCurves
